/** Response shapes from docs/API.md (admin audience). */

export type Role = 'OWNER' | 'ADMIN';
export type VehicleType = 'AUTO' | 'BIKE' | 'CAR';
export type DriverStatus = 'PENDING' | 'APPROVED' | 'REJECTED' | 'SUSPENDED';
export type RideStatus =
  | 'REQUESTED'
  | 'OFFERING'
  | 'ACCEPTED'
  | 'ARRIVED'
  | 'STARTED'
  | 'COMPLETED'
  | 'CANCELLED_BY_RIDER'
  | 'CANCELLED_BY_DRIVER'
  | 'CANCELLED_BY_ADMIN'
  | 'EXPIRED';
export type SosStatus = 'OPEN' | 'ACKNOWLEDGED' | 'RESOLVED';
export type DocumentKind = 'DRIVING_LICENCE' | 'VEHICLE_RC' | 'AADHAAR';

export const OPEN_STATUSES: RideStatus[] = ['REQUESTED', 'OFFERING', 'ACCEPTED', 'ARRIVED', 'STARTED'];
export const VEHICLE_TYPES: VehicleType[] = ['AUTO', 'BIKE', 'CAR'];

export interface Page<T> {
  items: T[];
  page: number;
  pageSize: number;
  total: number;
}

export interface Staff {
  id: string;
  email: string;
  name: string;
  role: Role;
  isActive: boolean;
  lastLoginAt: string | null;
  createdAt: string;
}

export interface TokenPair {
  accessToken: string;
  expiresIn: number;
  refreshToken: string;
  refreshExpiresAt: string;
}

export interface LatLng {
  lat: number;
  lng: number;
}
export interface Place extends LatLng {
  address: string | null;
}
export interface DriverLocation extends LatLng {
  heading: number | null;
  at: string | null;
}

export interface Vehicle {
  type: VehicleType;
  make: string;
  model: string;
  colour: string;
  plate: string;
  plateDisplay: string;
}

export interface FareBreakdown {
  baseFarePaise: number;
  distanceFarePaise: number;
  timeFarePaise: number;
  minimumTopUpPaise: number;
  bookingFeePaise: number;
  subtotalPaise: number;
  roundingPaise: number;
  totalPaise: number;
  distanceKm: number;
  durationMin: number;
  fareVersion?: number;
  isPlaceholderFare?: boolean;
}

export interface AdminRide {
  id: string;
  status: RideStatus;
  vehicleType: VehicleType;
  pickup: Place;
  drop: Place;
  distanceMeters: number;
  durationSeconds: number;
  farePaise: number;
  paymentMethod: string;
  cancellationFeePaise: number;
  cancelReason: string | null;
  requestedAt: string;
  acceptedAt: string | null;
  arrivedAt: string | null;
  startedAt: string | null;
  completedAt: string | null;
  endedAt: string | null;
  matchDeadline: string | null;
  quoteId: string;
  fareConfigId: string;
  fareBreakdown: FareBreakdown | null;
  offerRound: number;
  pinFailures: number;
  pickupNote?: string | null;
  discountPaise?: number;
  riderPaysPaise?: number;
  tipPaise?: number;
  paymentStatus?: string | null;
  commissionPercent?: number | null;
  commissionPaise?: number | null;
  driverEarningPaise?: number | null;
  rider: { id: string; name: string | null; phone: string; rating: number | null } | null;
  driver: {
    id: string;
    name: string | null;
    phone: string;
    rating: number | null;
    vehicle: Vehicle | null;
    location: DriverLocation | null;
  } | null;
}

export interface RideEvent {
  id: string;
  rideId: string;
  fromStatus: RideStatus | null;
  toStatus: RideStatus | null;
  type: 'TRANSITION' | 'OFFERS_SENT' | 'OFFER_DECLINED' | 'PIN_FAILED' | 'CASH_MISMATCH' | 'SOS';
  actorType: 'RIDER' | 'DRIVER' | 'ADMIN' | 'SYSTEM';
  actorId: string | null;
  note: string | null;
  data: Record<string, unknown> | null;
  createdAt: string;
}

export interface RideOffer {
  id: string;
  driverId: string;
  round: number;
  status: 'PENDING' | 'ACCEPTED' | 'DECLINED' | 'EXPIRED' | 'CANCELLED' | string;
  distanceMeters: number;
  offeredAt: string;
  expiresAt: string;
  respondedAt: string | null;
}

export interface Payment {
  method: string;
  status: string;
  amountDuePaise: number;
  amountCollectedPaise: number;
  tipPaise?: number;
  refundedPaise?: number;
  paidAt?: string | null;
  collectedAt?: string | null;
  failureReason?: string | null;
}

export interface Rating {
  id: string;
  byType: 'RIDER' | 'DRIVER';
  stars: number;
  tags: string[];
  comment: string | null;
  createdAt: string;
}

export interface RideDetail extends AdminRide {
  events: RideEvent[];
  offers: RideOffer[];
  payment: Payment | null;
  ratings: Rating[];
  sos: SosRecord[];
}

export interface DriverDocument {
  kind: DocumentKind;
  mimeType: string;
  sizeBytes: number;
  uploadedAt: string;
  url?: string;
  expiresAt?: string;
}

export interface DriverListItem {
  id: string;
  /** E.164; null for a driver who signed up by email and hasn't added a phone */
  phone: string | null;
  email?: string | null;
  name: string | null;
  status: DriverStatus;
  statusReason: string | null;
  isOnline: boolean;
  canGoOnline: boolean;
  vehicle: Vehicle | null;
  documents: DriverDocument[];
  missing: string[];
  rating: number | null;
  createdAt: string;
  location: DriverLocation | null;
}

export interface DriverDetail extends DriverListItem {
  completedRides: number;
  balancePaise: number;
}

export interface OnlineDriver {
  id: string;
  name: string | null;
  status: DriverStatus;
  vehicleType: VehicleType | null;
  plate: string | null;
  location: DriverLocation | null;
  activeRide: { id: string; status: RideStatus } | null;
}

export interface RiderListItem {
  id: string;
  /** E.164; null for a rider who signed in by email only */
  phone: string | null;
  name: string | null;
  email: string | null;
  rating: number | null;
  createdAt: string;
  isBlocked: boolean;
}

export interface RiderDetail extends RiderListItem {
  trustedContacts: { id: string; name: string; phone: string }[];
  ridesByStatus: Partial<Record<RideStatus, number>>;
}

export interface FareConfig {
  id: string;
  vehicleType: VehicleType;
  version: number;
  baseFarePaise: number;
  perKmPaise: number;
  perMinPaise: number;
  minimumFarePaise: number;
  bookingFeePaise: number;
  isPlaceholder: boolean;
  isActive: boolean;
  createdByAdminId: string | null;
  createdAt: string;
}

export type FareInput = Pick<
  FareConfig,
  'baseFarePaise' | 'perKmPaise' | 'perMinPaise' | 'minimumFarePaise' | 'bookingFeePaise'
>;

export interface Settings {
  commissionPercent: number;
  riderCancellationFeePaise: number;
  cancellationGraceSeconds: number;
  offerRadiusKm: number;
  offerBatchSize: number;
  offerTimeoutSeconds: number;
  matchingTimeoutSeconds: number;
  locationStaleSeconds: number;
  quoteTtlSeconds: number;
  updatedAt?: string;
  updatedByAdminId?: string | null;
}

export interface SosRecord {
  id: string;
  rideId: string | null;
  raisedByType: 'RIDER' | 'DRIVER';
  raisedById: string;
  lat: number | null;
  lng: number | null;
  note: string | null;
  rideSnapshot: {
    rideId?: string;
    status?: RideStatus;
    vehicleType?: VehicleType;
    pickup?: Place;
    drop?: Place;
    rider?: { id: string; name: string | null; phone: string };
    driver?: { id: string; name: string | null; phone: string; vehicle?: Vehicle | null; location?: DriverLocation | null };
  } | null;
  status: SosStatus;
  acknowledgedBy: string | null;
  acknowledgedAt: string | null;
  resolvedBy: string | null;
  resolvedAt: string | null;
  resolutionNote: string | null;
  createdAt: string;
}

export interface Dashboard {
  riders: number;
  drivers: { byStatus: Partial<Record<DriverStatus, number>>; online: number };
  rides: {
    open: Partial<Record<RideStatus, number>>;
    todayByStatus: Partial<Record<RideStatus, number>>;
    completedToday: number;
    faresTodayPaise: number;
  };
  sosOpen: number;
  at: string;
}

export interface AuditEntry {
  id: string;
  adminId: string | null;
  action: string;
  entityType: string;
  entityId: string;
  before: unknown;
  after: unknown;
  createdAt: string;
}

/** Realtime payloads (docs/API.md §9). */
export interface DriverLocationEvent {
  driverId: string;
  rideId: string | null;
  lat: number;
  lng: number;
  heading: number | null;
  at: string;
}
export interface DriverUpdatedEvent {
  id: string;
  status?: DriverStatus;
  statusReason?: string | null;
  isOnline?: boolean;
  lat?: number;
  lng?: number;
}
export interface RideUpdatedEvent {
  id: string;
  status: RideStatus;
  ride?: AdminRide;
  truncated?: boolean;
}

// ───── cash settlements and ratings (docs/API.md §6 "Cash settlements", "Ratings and complaints") ─────

export interface SettlementRow {
  driver: { id: string; name: string | null; phone: string | null; status: DriverStatus | null; isOnline: boolean; vehicle: Vehicle | null };
  /** < 0: the driver holds Zoomo Rides' cash; > 0: Zoomo Rides owes the driver */
  balancePaise: number;
  toCollectPaise: number;
  toPayPaise: number;
  lastSettledAt: string | null;
}
export interface SettlementPage extends Page<SettlementRow> {
  totals: { driversOwePaise: number; zoomoOwesPaise: number };
}
export type SettleDirection = 'FROM_DRIVER' | 'TO_DRIVER';

export interface LedgerEntry {
  id: string;
  rideId: string | null;
  type: string;
  amountPaise: number;
  note: string | null;
  createdAt: string;
}

export interface RatingRow {
  id: string;
  rideId: string;
  byType: 'RIDER' | 'DRIVER';
  stars: number;
  tags: string[];
  comment: string | null;
  createdAt: string;
  complaint: boolean;
  rider: { id: string; name: string | null } | null;
  driver: { id: string; name: string | null } | null;
  ride: { id: string; vehicleType: VehicleType; farePaise: number; pickupAddress: string | null; dropAddress: string | null; completedAt: string | null };
}
export interface RatingPage extends Page<RatingRow> {
  summary: { ofDrivers: { count: number; average: number | null }; ofRiders: { count: number; average: number | null } };
}

// ───── commission, milestones and rewards (docs/API.md §6) ─────

export interface CommissionRule {
  id: string;
  vehicleType: VehicleType | null;
  percent: number;
  effectiveFrom: string;
  inForce: boolean;
  note: string | null;
  createdByAdminId: string | null;
  createdAt: string;
}
export interface Commissions {
  current: { vehicleType: VehicleType; percent: number; ruleId: string | null; source: 'TYPE' | 'GLOBAL' | 'DEFAULT' }[];
  upcoming: CommissionRule[];
  rules: CommissionRule[];
  maxPercent: number;
}

export type RewardAudience = 'RIDER' | 'DRIVER';
export type RewardKind = 'DRIVER_CASH_BONUS' | 'RIDER_RIDE_CREDIT' | 'RIDER_DISCOUNT_PERCENT';
export interface Milestone {
  id: string;
  audience: RewardAudience;
  threshold: number;
  title: string;
  description: string | null;
  rewardKind: RewardKind;
  rewardPaise: number;
  rewardPercent: number | null;
  rewardMaxPaise: number | null;
  isActive: boolean;
  isPlaceholder: boolean;
  createdByAdminId: string | null;
  createdAt: string;
  earnedCount: number;
}
export interface EarnedReward {
  id: string;
  milestoneId: string;
  title: string;
  kind: RewardKind;
  amountPaise: number;
  percent: number | null;
  maxPaise: number | null;
  status: 'AVAILABLE' | 'APPLIED' | 'USED' | 'CREDITED' | 'VOID';
  triggerRideId: string | null;
  appliedRideId: string | null;
  usedAt: string | null;
  createdAt: string;
  audience: RewardAudience;
  subjectId: string;
}

// ───── driver payouts (docs/API.md §4a, §6 "Driver payouts and balances") ─────

export type PayoutMethod = 'UPI' | 'BANK' | 'CASH';
export type PayoutStatus = 'PENDING' | 'PAID' | 'CANCELLED';
export interface Payout {
  id: string;
  amountPaise: number;
  method: PayoutMethod;
  status: PayoutStatus;
  reference: string | null;
  note: string | null;
  createdAt: string;
  paidAt: string | null;
  cancelledAt: string | null;
  cancelReason: string | null;
  driver?: { id: string; name: string | null; phone: string | null };
}
export interface MoneyTotals {
  balancePaise: number;
  pendingPayoutsPaise: number;
  availableToPayPaise: number;
  toCollectPaise: number;
}
export interface BalanceRow extends MoneyTotals {
  driver: { id: string; name: string | null; phone: string | null; email: string | null; status: DriverStatus | null; isOnline: boolean; vehicle: Vehicle | null };
  lastPayoutAt: string | null;
  lastHandInAt: string | null;
}
export interface BalancePage extends Page<BalanceRow> {
  totals: { driversOwePaise: number; zoomoOwesPaise: number; pendingPayoutsPaise: number };
}
export type MoneyEntry = LedgerEntry & { payoutId?: string | null };
export interface LedgerPage extends Page<MoneyEntry>, Partial<Omit<MoneyTotals, 'balancePaise'>> {
  balancePaise: number;
}
export interface Statement {
  weekStart: string;
  weekEnd: string;
  isCurrent: boolean;
  openingPaise: number;
  rides: number;
  faresPaise: number;
  commissionPaise: number;
  earningsPaise: number;
  cashCollectedPaise: number;
  tipsPaise: number;
  cancellationFeesPaise: number;
  rewardsPaise: number;
  adjustmentsPaise: number;
  payoutsPaise: number;
  cashHandedInPaise: number;
  closingPaise: number;
}
export interface StatementDetail extends Statement {
  entries: MoneyEntry[];
  payouts: Payout[];
}
